import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { isBlank } from 'underscore.string';
import { useRouter } from 'next/compat/router';
import { useApolloClient } from '@apollo/client';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useHideCategoryTiles } from 'shared/hooks/use-hide-category-tiles';

import { HorizontalDivider } from 'components/core';
import DisclaimerText from 'src/dispensary/components/disclaimer-text';
import CategoryTilesGrid from 'components/category-tiles-grid';
import { MainContent } from 'components/skip-to-main';
import { MainDispensaryLayout } from 'components/layouts/main-dispensary-layout';
import { DesktopOnly, MobileOnly } from 'shared/components';

import useCart from 'hooks/use-cart';
import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';
import useDispensaryMenuSections from 'src/dispensary/hooks/use-dispensary-menu-sections';
import { getHtmlPlainText } from 'shared/utils/misc-utils';
import { AddressValidationReason, validateAddressLimits } from 'src/state/actions/address';
import { getStoreRoutingAttemptFingerprint } from 'src/state/actions/cart';
import { ORDER_TYPE_DELIVERY, ORDER_TYPE_PICKUP } from 'shared/constants';

import { BreadCrumbs } from 'src/components/bread-crumbs';
import { HomepageCarouselSections } from 'components/homepage-carousel-sections';
import CategoriesCarouselSlider from 'components/categories-carousel-slider';
import { PersonalizedProducts } from 'components/carousels/products-carousels/personalized-products-carousel';
import { DutchieLoyaltyWidget } from 'src/components/widgets/dutchie-loyalty-widget';
import { LoginCta } from 'src/components/ctas';
import { useLoyalty } from 'src/components/widgets/loyalty-tiers/use-loyalty';

import { useProductsForHomePage } from 'src/dispensary/hooks/use-products-for-home-page';
import { HomePageSponsoredBrandCarousel } from 'src/components/carousels/ads-carousels/home-page-sponsored-brand-carousel';
import { useAdsEnabled } from 'src/utils/ads/hooks/use-ads-enabled';
import { useShowBuyItAgainCarousel } from 'src/dispensary/hooks/use-show-buy-it-again-carousel';
import { BuyItAgainCarousel } from 'src/components/carousels/products-carousels/buy-it-again-carousel';
import { useStoreRoutingOffer } from 'src/hooks/use-store-routing-offer';
import { didStoreRoutingHandleSource } from 'src/constants/store-routing';

export default function DispensaryPage() {
  const apolloClient = useApolloClient();
  const Cart = useCart();
  const UI = useUI();
  const { dispensary } = useDispensary();
  const { categories } = useDispensaryCategories();
  const router = useRouter();
  const orderType = useObserver(() => Cart.simplifiedOrderType);
  const [showCategoriesBorder, setShowCategoriesBorder] = useState(true);
  const dispensaryId = dispensary?.id;
  const { redemptionType, hasTiers } = useLoyalty();
  const adsEnabled = useAdsEnabled();
  const hideCategoryTiles = useHideCategoryTiles(dispensary);

  const flags = useFlags();
  const offerStoreRoute = useStoreRoutingOffer();
  const lastRoutingOfferAttemptRef = useRef(null);

  const showBuyItAgainCarousel = useShowBuyItAgainCarousel();

  const productsForHomePage = useProductsForHomePage({ dispensaryId });
  const { personalizedProductsQuery } = productsForHomePage;
  const personalizedProducts = personalizedProductsQuery?.data?.getPersonalizedProducts?.products;

  // A dispensary can now add a RECOMMENDED_FOR_YOU homepage section (PF-5771), which
  // HomepageCarouselSections renders below. Avoid rendering this same carousel twice:
  // only show it unconditionally here when no such section is configured, same as it
  // always has for every dispensary that hasn't opted into the configurable section.
  const { menuSections, loading: menuSectionsLoading } = useDispensaryMenuSections();
  const hasRecommendedForYouSection = menuSections?.some((section) => section.sectionType === 'RECOMMENDED_FOR_YOU');
  const showLegacyPersonalizedProducts = !menuSectionsLoading && !hasRecommendedForYouSection;

  const sponsoredBrandCarouselEnabled =
    flags['growth.ads.home-page-sponsored-brand-carousel.config']?.[UI.variant] ?? false;
  const showHomePageSponsoredBrandCarousel = adsEnabled && sponsoredBrandCarouselEnabled;

  useEffect(() => {
    if (router?.query?.cName) {
      UI.setEmbeddedCName(router?.query.cName);
    }
  }, [UI, router]);

  useEffect(() => {
    async function checkAddress() {
      if (!Cart.hasDeliveryAddress() || orderType === ORDER_TYPE_PICKUP) {
        return false;
      }

      const address = Cart.currentAddress;
      const attemptFingerprint = dispensary
        ? getStoreRoutingAttemptFingerprint(dispensary, address, Cart.isMedical)
        : null;
      const isWithinDeliveryBoundary = await validateAddressLimits(Cart, apolloClient, address, dispensary);
      if (
        orderType === ORDER_TYPE_DELIVERY &&
        isWithinDeliveryBoundary.reason === AddressValidationReason.OUTSIDE_DELIVERY_AREA
      ) {
        if (attemptFingerprint && attemptFingerprint === lastRoutingOfferAttemptRef.current) {
          return isWithinDeliveryBoundary.success;
        }
        if (attemptFingerprint) {
          const routingOutcome = await offerStoreRoute({
            source: 'menu_home',
            address,
            currentDispensary: dispensary,
            isStillEligible: () =>
              Cart.simplifiedOrderType === ORDER_TYPE_DELIVERY &&
              getStoreRoutingAttemptFingerprint(dispensary, Cart.currentAddress, Cart.isMedical) === attemptFingerprint,
          });
          if (routingOutcome === 'declined') {
            lastRoutingOfferAttemptRef.current = attemptFingerprint;
          }
          if (didStoreRoutingHandleSource(routingOutcome)) {
            return isWithinDeliveryBoundary.success;
          }
        }

        UI.openShowOutsideDeliveryAreaModal();
      }
      return isWithinDeliveryBoundary.success;
    }
    checkAddress();
  }, [orderType, Cart, dispensary, apolloClient, UI, offerStoreRoute]);

  // Don't use Categories bottom border if an ads or personalization carousel is loading below it
  useEffect(() => {
    if (personalizedProducts?.length > 0) {
      setShowCategoriesBorder(false);
    }
  }, [personalizedProducts]);

  const disclaimerIsBlank = useMemo(
    () =>
      !dispensary?.embedSettings?.disclaimerTextHtml ||
      isBlank(getHtmlPlainText(dispensary?.embedSettings?.disclaimerTextHtml)),
    [dispensary?.embedSettings?.disclaimerTextHtml]
  );

  if (!dispensary) {
    return null;
  }

  return (
    <MainContent>
      <ContentWrapper>
        <BreadCrumbsContainer>
          <BreadCrumbs includeLeftPadding={false} />
        </BreadCrumbsContainer>

        {!hideCategoryTiles && (
          <div data-testid='homepage-categories'>
            <MobileOnly mediaQuery='xs' display='block'>
              <CategoryTilesGrid categories={categories} mb='40px' />
              {showCategoriesBorder && <HorizontalDivider />}
            </MobileOnly>

            <DesktopOnly mediaQuery='sm'>
              <CategoriesCarouselSlider borderBottom={showCategoriesBorder} />
            </DesktopOnly>
          </div>
        )}

        <DutchieLoyaltyWidget hasTiers={hasTiers} redemptionType={redemptionType ?? ''} displayCTA />

        <LoginCta margin='20px auto 40px' hideIfNeitherEnabled />

        {showHomePageSponsoredBrandCarousel && <HomePageSponsoredBrandCarousel />}

        {showBuyItAgainCarousel && <BuyItAgainCarousel />}

        {showLegacyPersonalizedProducts && <PersonalizedProducts {...productsForHomePage} />}

        <HomepageCarouselSections dispensary={dispensary} />

        {!disclaimerIsBlank && <DisclaimerText m='50px auto' html={dispensary.embedSettings.disclaimerTextHtml} />}
      </ContentWrapper>
    </MainContent>
  );
}

DispensaryPage.layout = MainDispensaryLayout;

const ContentWrapper = styled.div`
  margin-top: 20px;
`;

const BreadCrumbsContainer = styled.div`
  margin: 0 auto 16px;

  /* Mimic sizing from carousels/category tiles above */
  @media (min-width: 600px) {
    max-width: 534.5px;
  }

  @media (min-width: 750.5px) {
    max-width: 750.5px;
  }

  @media (min-width: 966.5px) {
    max-width: 966.5px;
  }

  @media (min-width: 1187px) {
    max-width: 1187px;
  }
`;
